import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

// Utils
import { isNil } from 'lodash';
import { useTranslation } from 'react-i18next';

// Components
import TabsBar from '../TabsBar/TabsBar';
import PageSection from '../PageSection/PageSection';
import Breadcrumb from '../Breadcrumb/Breadcrumb';
import SkeletonLoader from '../../molecules/SkeletonLoader/SkeletonLoader';

function LayoutWithTabs({
  tabs, title, breadcrumb, isFetching, skeletonLoader, moreContent,
}) {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState(0);

  const handleScrollToSection = (index) => {
    if (!isNil(index)) {
      const activeSectionRef = tabs[index]?.sectionRef;

      if (activeSectionRef?.current) {
        activeSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      tabs?.forEach((tab, index) => {
        const tabTitleRef = tab?.titleRef;

        if (tabTitleRef?.current) {
          const distanceFromtop = tabTitleRef.current.getBoundingClientRect()?.top;

          if (distanceFromtop < 5 && distanceFromtop > -5) {
            setActiveTab(index);
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className="d-flex a-stretch mobile-d-col mobile-p-t-20">
      <TabsBar
        items={tabs}
        handleScrollToSection={handleScrollToSection}
        activeTab={activeTab}
      />

      <div className="flex-auto p-40 mobile-p-0 mobile-m-t-30">
        {breadcrumb ? (
          <Breadcrumb
            title={title}
            breadcrumb={breadcrumb}
          />
        ) : null}

        <h1 className="fs-32 c-primary-900 fw-500 m-b-20 mobile-fs-24">
          {title}
        </h1>

        <div className="w-100">
          {isFetching ? (
            skeletonLoader || <SkeletonLoader />
          ) : (
            tabs?.map((tab, index) => (
              <PageSection
                titleRef={tab.titleRef}
                sectionRef={tab.sectionRef}
                index={index}
                key={index}
                title={t(tab.label)}
                icon={tab.icon}
              >
                {tab.component}
              </PageSection>
            ))
          )}
        </div>

        {moreContent}
      </div>
    </div>
  );
}

LayoutWithTabs.propTypes = {
  tabs: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    component: PropTypes.node,
    icon: PropTypes.node,
    titleRef: PropTypes.shape({
      current: PropTypes.shape({}),
    }),
    sectionRef: PropTypes.shape({
      current: PropTypes.shape({}),
    }),
  })).isRequired,
  breadcrumb: PropTypes.arrayOf(PropTypes.shape({})),
  title: PropTypes.string,
  isFetching: PropTypes.bool,
  skeletonLoader: PropTypes.node,
  moreContent: PropTypes.node,
};

LayoutWithTabs.defaultProps = {
  breadcrumb: null,
  title: null,
  isFetching: false,
  skeletonLoader: null,
  moreContent: null,
};

export default LayoutWithTabs;
